import {
  CloudOff,
  Lock,
  LockOpen,
  RefreshCw,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useTillCurrent } from "../api/queries";
import { cn } from "../lib/cn";
import { useOnline } from "../offline/connectivity";
import { syncNow, useSyncState } from "../offline/syncState";

/** Top-bar pills (§6.1): Till open/closed (links to /till), Sync now, Online/Offline with the
 * unsynced count (§2.2, §16.1). Shared by the back-office and kiosk shells. */

export function TillPill({ compact }: { compact?: boolean }) {
  const q = useTillCurrent();
  if (q.isPending)
    return (
      <span
        className="skeleton h-8 w-[104px] !rounded-full"
        aria-hidden="true"
      />
    );
  const open = !!q.data;
  return (
    <Link
      to="/till"
      className={cn(
        "inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-bold ring-1 ring-inset transition-[box-shadow,filter] duration-150 hover:brightness-[0.97] dark:hover:brightness-125",
        open
          ? "bg-success-tint text-success-ink ring-success/25"
          : "bg-warning-tint text-warning ring-warning/30",
      )}
      aria-label={
        open ? "Till open — view till session" : "Till closed — open the till"
      }
    >
      {open ? (
        <span className="live-dot" aria-hidden="true" />
      ) : (
        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {!compact && (open ? "Till open" : "Till closed")}
    </Link>
  );
}

/** The kiosk padlock (§7.1): green when a till is open, amber when not, links to /till. */
export function TillLock() {
  const q = useTillCurrent();
  const open = !!q.data;
  return (
    <Link
      to="/till"
      aria-label={
        q.isPending
          ? "Till status loading"
          : open
            ? "Till open — view till session"
            : "No till open — open the till"
      }
      className={cn(
        "inline-flex h-11 w-11 items-center justify-center rounded-[12px] transition-colors",
        q.isPending
          ? "text-neutral-300"
          : open
            ? "bg-success-tint text-success ring-1 ring-inset ring-success/25 hover:brightness-[0.97]"
            : "bg-warning-tint text-warning ring-1 ring-inset ring-warning/30 hover:brightness-[0.97]",
      )}
    >
      {open ? (
        <LockOpen className="h-[18px] w-[18px]" />
      ) : (
        <Lock className="h-[18px] w-[18px]" />
      )}
    </Link>
  );
}

export function SyncButton({ iconOnly }: { iconOnly?: boolean }) {
  const online = useOnline();
  const s = useSyncState();
  const disabled = !online || s.syncing;
  return (
    <button
      type="button"
      onClick={() => void syncNow()}
      disabled={disabled}
      title={
        !online ? "Sync is unavailable while offline" : (s.message ?? undefined)
      }
      aria-label={iconOnly ? (s.syncing ? "Syncing…" : "Sync now") : undefined}
      className={cn(
        "btn btn-secondary btn-sm relative !rounded-full",
        iconOnly && "btn-icon",
      )}
    >
      <RefreshCw
        className={cn(s.syncing && "animate-spin")}
        aria-hidden="true"
      />
      {!iconOnly && (s.syncing ? "Syncing…" : "Sync now")}
      {s.pending > 0 && (
        <span
          className="absolute -right-1.5 -top-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-warning px-1 text-2xs font-bold text-white ring-2 ring-[var(--surface)] tnum"
          aria-label={`${s.pending} unsynced`}
        >
          {s.pending}
        </span>
      )}
    </button>
  );
}

export function OnlinePill({ compact }: { compact?: boolean }) {
  const online = useOnline();
  const s = useSyncState();
  const { user } = useAuth();
  const unsynced = s.pending + s.failed;
  // Managers can open the review screen from the count (failed sales need their decision).
  const canReview = user?.role === "ADMIN" || user?.role === "MANAGER";
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn(
        "inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-bold ring-1 ring-inset",
        online
          ? "bg-success-tint text-success-ink ring-success/25"
          : "bg-danger-tint text-danger-ink ring-danger/30",
      )}
    >
      {online ? (
        <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {compact ? (
        <span className="sr-only">{online ? "Online" : "Offline"}</span>
      ) : online ? (
        "Online"
      ) : (
        "Offline"
      )}
      {unsynced > 0 &&
        (canReview ? (
          <Link
            to="/sales/queue"
            className="inline-flex items-center gap-1 border-l border-current/25 pl-2 underline-offset-2 hover:underline"
          >
            <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="tnum">{unsynced}</span>
            <span className="sr-only">unsynced sales — review</span>
          </Link>
        ) : (
          <span className="inline-flex items-center gap-1 border-l border-current/25 pl-2">
            <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="tnum">{unsynced}</span>
            <span className="sr-only">unsynced sales</span>
          </span>
        ))}
    </span>
  );
}
